import { BaseElementToSimpleSapElementMapper } from '../baseElementToSimpleSapElementMapper';
import Element, { ElementType } from '@/models/element';
import { ISimpleSapElement, SimpleSapElement } from '@/models/api/models/simpleSapElement';
import store from '@/store';
import { DeskLegsToSimpleSapElementMapper } from '../desk/deskLegsToSimpleSapElementMapper';
import { IShader } from '@/models/shaders/shader';

export class BedAllElementToSimpleSapElementMapper
    extends DeskLegsToSimpleSapElementMapper {

    private readonly dynamicLegs = [
        'SDGS',
        'SDKS',
        'SDSS',
        'SMDG',
        'SMGS',
        'NMGP',
    ];
    private readonly floors = new Map([
        ['ShaderLiftedFrame', 4],
        ['ShaderFloorFrame', 1],
        ['ShaderFloorWithFlexibleSlatsFrame', 3],
    ]);
    private readonly sizes = new Map([[90, 1], [120, 2], [140, 3], [160, 4], [180, 5]]);
    private size = 0;
    private floorShader: IShader;

    public map(element: Element): ISimpleSapElement[] {
        this.floorShader = store.getters.shaders.selectedFrame;
        this.size = Number(this.body.model.split('_')[1]);
        if (!this.size) { this.size = 160; }
        const result: ISimpleSapElement[] = [];
        result.push(this.createBody(element));
        if (this.sizes.get(this.size) !== 1) {
            result.push(this.createBottomGable(element));
        }
        result.push(this.createUpperGable());
        if (this.hasContainer) {
            result.push(...this.createContainer(element));
        }
        if (this.hasSkirtingBoards) {
            result.push(this.createSkirtingBoards(element));
        }
        result.push(this.createLegs());
        result.push(...this.createFloor());
        return result;
    }

    private get hasContainer(): boolean {
        return this.floorShader.key === 'ShaderLiftedFrame';
    }

    private get hasSkirtingBoards(): boolean {
        return this.floorShader.key !== 'ShaderLiftedFrame';
    }

    private createBody(element: Element): ISimpleSapElement {
        const dim2 = this.size === 90 ? 1 : 2;
        const type = this.formatType('OSK', 8, dim2);
        const color = this.toColor(element);
        return SimpleSapElement.ELEMENT(type, color);
    }

    private createBottomGable(element: Element): ISimpleSapElement {
        const dim2 = this.sizes.get(this.size);
        const type = this.formatType('SZN', 8, dim2);
        const color = this.toColor(element);
        return SimpleSapElement.ELEMENT(type, color);
    }

    private createUpperGable(): ISimpleSapElement {
        const elements: Element[] = store.getters.scene.elements;
        const bedhead = elements.find((x) => x.type === ElementType.BEDHEAD);
        const dim2 = this.sizes.get(this.size);
        const type = this.formatType('SZW', 8, dim2);
        const color = this.toColor(bedhead);
        return SimpleSapElement.ELEMENT(type, color);
    }

    private createContainer(element: Element): ISimpleSapElement[] {
        const dim1 = 8;
        const dim2 = this.sizes.get(this.size);
        const type = this.formatType('POJ', dim1, dim2);
        const type2 = this.formatType('POE', dim1, dim2);
        const color = 'BIA';
        return [
            SimpleSapElement.ELEMENT(type, color),
            SimpleSapElement.ELEMENT(type2, color),
        ];
    }

    private createSkirtingBoards(element: Element): ISimpleSapElement {
        const dim2 = this.sizes.get(this.size);
        const type = this.formatType('LPP', 8, dim2);
        const color = 'BIA';
        return SimpleSapElement.ELEMENT(type, color);
    }

    private createLegs(): ISimpleSapElement {
        const elements: Element[] = store.getters.scene.elements;
        const legs = elements.find((x) => x.type === ElementType.LEGS);
        const code = this.toLegsType(legs);
        const dim1 = code === this.metalLegs ? 1 : 8;
        const dim2 =
            code === this.metalLegs
                ? 3
                : this.dynamicLegs.some((x) => code.includes(x))
                    ? this.sizes.get(this.size)
                    : 1;
        const type = this.formatType(code, dim1, dim2);
        return SimpleSapElement.LEG(type, '');
    }

    private createFloor(): ISimpleSapElement[] {
        const dim1 = this.sizes.get(this.size);
        const dim2 = this.floors.get(this.floorShader.key);
        const type = this.formatType('POD', dim1, dim2);
        if (dim1 === 5 && dim2 === 3) {
            const t = this.formatType('POD', 1, 3);
            return [
                SimpleSapElement.ELEMENT(t, ''),
                SimpleSapElement.ELEMENT(t, ''),
            ];
        }
        if (dim1 === 4 && dim2 === 3) {
            return [
                SimpleSapElement.ELEMENT(type, ''),
                SimpleSapElement.ELEMENT(type, ''),
            ];
        }
        return [SimpleSapElement.ELEMENT(type, '')];
    }
}
